/* eslint-disable react-refresh/only-export-components */
import type { User } from '@/shared/types/user';
import { capitalize } from '@/lib/strings';
import { getInitials } from '@/shared/lib/names';
import { UserActions } from './actions';
import { StandardIcon } from '@/components/common/icon';
import { IconIds } from '@/lib/constants';
import { Avatar } from '@/components/ui';

export type ColumnKey = keyof User['data'] | 'access' | 'actions';

export function createColumn({
  key,
  displayName = capitalize(key),
  align = 'start',
}: {
  key: ColumnKey;
  displayName?: string;
  align?: 'start' | 'center' | 'end';
}) {
  return {
    key,
    displayName,
    align,
  };
}

export function makeColumnLookup(cols: ColumnInfo[]) {
  return cols.reduce(
    (m, v) => {
      m[v.key] = v;

      return m;
    },
    {} as Record<ColumnKey, ColumnInfo>,
  );
}

export type ColumnInfo = ReturnType<typeof createColumn>;
export type ActionKey = 'edit' | 'archive' | 'invite';

export function CellDisplay({
  item,
  col,
  onAction,
}: {
  item: User;
  col: ColumnInfo;
  onAction: (item: User, action: ActionKey) => void;
}) {
  if (col.key === 'displayName') {
    const name = item.data.displayName || '';
    const initials =
      getInitials(name) || item.data.email?.slice(0, 2).toUpperCase() || '?';
    return (
      <div className='flex items-center gap-3'>
        <Avatar
          name={name}
          getInitials={() => initials}
          className={getAvatarColorClass(name)}
          size='sm'
        />
        <span className='font-medium text-slate-800'>
          {name || item.data.email}
        </span>
      </div>
    );
  }
  if (col.key === 'access') {
    return <UserAccess data={item.data} />;
  } else if (col.key === 'actions') {
    return <UserActions item={item} onAction={onAction} />;
  }

  return <div>{String(item.data[col.key])}</div>;
}

/** Avatar background color variant from name (same as People 2 view) */
function getAvatarColorClass(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = (hash << 5) - hash + name.charCodeAt(i);
    hash |= 0;
  }
  const v = Math.abs(hash) % 3;
  if (v === 0) {
    return 'bg-blue-100 text-blue-800';
  }
  if (v === 1) {
    return 'bg-slate-200 text-slate-700';
  }
  return 'bg-amber-100 text-amber-800';
}

function UserAccess({ data }: { data: User['data'] }) {
  // const Placeholder = () => <div className='w-[20px]'></div>;
  // TODO tooltip around icons to explain what role they represent
  const AccessIcon = ({ icon, access }: { icon: string; access: boolean }) => {
    if (access) {
      return <StandardIcon id={icon} />;
    }

    return <StandardIcon id={icon} className='!text-default-200' />;
  };

  return (
    <div className='flex flex-row items-center justify-center gap-1'>
      <AccessIcon icon={IconIds.admin} access={data.admin} />
      <AccessIcon icon={IconIds.people} access={data.people} />
      <AccessIcon icon={IconIds.workflows} access={data.workflows} />
      <AccessIcon
        icon={IconIds.communications}
        access={Boolean(data.communications)}
      />
    </div>
  );
}
